import { TFunction } from 'i18next'
import { AlertTypeDeliverySettings, AlertingScheduleOfOneAlertType } from '../../../../app/api'
import { EMAIL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'

export type AlertType = AlertingScheduleOfOneAlertType['type']
export type DeliveryMode = AlertingScheduleOfOneAlertType['mode']
export type Frequency = NonNullable<AlertingScheduleOfOneAlertType['frequency']>
export type Send = AlertingScheduleOfOneAlertType['sends'][number]
export type RecipientChannel = 'email' | 'tg'

export const ALERT_TYPES: AlertType[] = ['affiliates', 'tm_infringements']

export const FREQUENCIES: Frequency[] = [
  'once_per_day',
  'multiple_per_day',
  'once_per_week',
  'multiple_per_week',
]

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

export const DEFAULT_SEND_TIME = '09:00'

// Пресет, который подставляется при переходе с дефолтного Instant на Scheduled. Зона — London,
// а не Moscow: дефолт не должен выдавать связь с РФ
export const DEFAULT_SCHEDULE = {
  frequency: 'multiple_per_day' as Frequency,
  timezone: 'Europe/London',
  sends: [{ time: '07:00' }, { time: '12:00' }] as Send[],
}

export const isWeekly = (frequency?: Frequency | null) =>
  frequency === 'once_per_week' || frequency === 'multiple_per_week'

export const isMultiple = (frequency?: Frequency | null) =>
  frequency === 'multiple_per_day' || frequency === 'multiple_per_week'

/** Поиск зоны: регистр и подчёркивания не важны — «ho chi» находит Asia/Ho_Chi_Minh */
export const matchesTimezone = (text: string, search: string): boolean => {
  const normalize = (value: string) => value.toLowerCase().replace(/_/g, ' ')

  return normalize(text).includes(normalize(search.trim()))
}

export const TIME_OPTIONS: IOption[] = Array.from({ length: 48 }, (_, index) => {
  const time = `${String(Math.floor(index / 2)).padStart(2, '0')}:${index % 2 ? '30' : '00'}`

  return { value: time, text: time }
})

/** Список отправок под новую частоту: дубли не переносятся, недельный режим собирается заново */
export const sendsForFrequency = (frequency: Frequency, sends: Send[]): Send[] => {
  const firstTime = sends[0]?.time ?? DEFAULT_SEND_TIME

  switch (frequency) {
    case 'once_per_day':
      return [{ time: firstTime }]
    case 'multiple_per_day': {
      const times = Array.from(new Set(sends.map((send) => send.time)))

      return times.length ? times.map((time) => ({ time })) : [{ time: firstTime }]
    }
    case 'once_per_week':
      return [{ weekday: sends[0]?.weekday ?? WEEKDAYS[0], time: firstTime }]
    case 'multiple_per_week':
      return WEEKDAYS.map((weekday) => ({ weekday, time: DEFAULT_SEND_TIME }))
  }
}

/** Следующее свободное время с шагом 30 минут после последней отправки */
export const nextFreeTime = (sends: Send[]): string => {
  const used = sends.map((send) => send.time)
  const last = TIME_OPTIONS.findIndex(({ value }) => value === sends[sends.length - 1]?.time)

  for (let step = 1; step <= TIME_OPTIONS.length; step++) {
    const time = TIME_OPTIONS[(last + step) % TIME_OPTIONS.length].value as string

    if (!used.includes(time)) return time
  }

  return DEFAULT_SEND_TIME
}

/** Индексы отправок, повторяющих одну из предыдущих по времени (и дню для недельных частот) */
export const duplicateSendIndexes = (frequency: Frequency | null, sends: Send[]): number[] => {
  const seen = new Set<string>()
  const duplicates: number[] = []

  sends.forEach((send, index) => {
    // незаполненная отправка — не дубль: её и так не даст сохранить проверка заполненности
    if (!send.time || (isWeekly(frequency) && !send.weekday)) return

    const key = isWeekly(frequency) ? `${send.weekday}|${send.time}` : send.time

    if (seen.has(key)) duplicates.push(index)
    seen.add(key)
  })

  return duplicates
}

/** Подпись чипа и инлайн-кнопки: только режим или периодичность */
export const scheduleLabel = (
  t: TFunction,
  { mode, frequency, sends }: Pick<AlertTypeDeliverySettings, 'mode' | 'frequency' | 'sends'>,
): string => {
  if (mode === 'instant' || !frequency) return t('components.alertingSchedule.periodicity.instant')

  return t(`components.alertingSchedule.periodicity.${frequency}`, { count: sends.length })
}

/** Тултип с полным расписанием: частота, отправки и часовой пояс */
export const scheduleTooltip = (
  t: TFunction,
  {
    mode,
    frequency,
    timezone,
    sends,
  }: Pick<AlertTypeDeliverySettings, 'mode' | 'frequency' | 'timezone' | 'sends'>,
  timezoneLabel: (timezone?: string | null) => string,
): string[] => {
  if (mode === 'instant' || !frequency) return [t('components.alertingSchedule.instantDescription')]

  const sendsText = sends
    .map(({ weekday, time }) =>
      isWeekly(frequency) && weekday
        ? `${t(`components.alertingSchedule.weekdaysShort.${weekday}`)} · ${time}`
        : time,
    )
    .join(', ')

  return [
    t(`components.alertingSchedule.frequencies.${frequency}`),
    [sendsText, timezoneLabel(timezone)].filter(Boolean).join(' · '),
  ]
}

export const isValidRecipient = (channel: RecipientChannel, value: string): boolean =>
  channel === 'email'
    ? EMAIL_REGEX.test(value)
    : /^-?\d+$/.test(value) || /^@[A-Za-z0-9_]{5,32}$/.test(value)
